import React from 'react';
import CheckboxesTags from '../MultiSelect';
import { Tabs, Tab, Box } from '@mui/material';
import 'react-datepicker/dist/react-datepicker.css';
import { ThemePalette } from 'mui.theme';
import './FilterBar.css';
import CustomDatePicker from './CustomDatePicker';
import GlencoreButton from './GlencoreButton';
import ColorCodings from './ColorCodings';
import { stringToHexColor } from 'utils/glencore';

interface FiltersBarProps {
  locations: string[];
  selectedLocations: string[];
  onLocationsChange: (options: string[]) => void;
  assets: string[];
  selectedAssets: string[];
  onAssetsChange: (options: string[]) => void;
  activityOptions: string[];
  selectedActivities: string[];
  onActivitiesChange: (options: string[]) => void;
  batteryOptions: string[];
  selectedBatteryInsights: string[];
  onBatteryInsightsChange: (options: string[]) => void;
  selectedStartDate: Date;
  selectedEndDate: Date;
  onDateChange: (range: [Date, Date]) => void;
  loading: boolean;
  onFetch: () => void;
  darkMode: boolean;
  timelineView: 'location' | 'asset';
  onTimelineViewChange: (view: 'location' | 'asset') => void;
  locationColors?: Record<string, string>;
  assetColors?: Record<string, string>;
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  locations,
  selectedLocations,
  onLocationsChange,
  assets,
  selectedAssets,
  onAssetsChange,
  activityOptions,
  selectedActivities,
  onActivitiesChange,
  batteryOptions,
  selectedBatteryInsights,
  onBatteryInsightsChange,
  selectedStartDate,
  selectedEndDate,
  onDateChange,
  loading,
  onFetch,
  darkMode,
  timelineView,
  onTimelineViewChange,
  locationColors = {},
  assetColors = {},
}) => {
  return (
    <>
      <div className="w-full mb-2 flex justify-between items-center">
        <Tabs
          value={timelineView}
          onChange={(_, v) => {
            onTimelineViewChange(v as 'location' | 'asset');
          }}
          aria-label="Timeline View Tabs"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
              px: 2,
              py: 1,
              color: (theme) =>
                darkMode
                  ? ThemePalette.typography.white
                  : theme.palette.text.secondary,
            },
            '& .MuiTab-root.Mui-selected': {
              bgcolor: (theme) => theme.palette.primary.main,
              color: (theme) => theme.palette.primary.contrastText,
            },
          }}
        >
          <Tab disableRipple value="location" label="Location Timeline" />
          <Tab disableRipple value="asset" label="Asset Timeline" />
        </Tabs>
        <ColorCodings timelineView={timelineView} />
      </div>
      <Box
        className="w-full flex flex-wrap justify-between items-end mb-2 gap-4 pb-2 relative"
        sx={{
          backgroundColor: darkMode
            ? 'rgba(23, 42, 70, 0.5)'
            : 'rgba(255, 255, 255, 0.7)',
          borderRadius: '10px',
          padding: '16px',
          boxShadow: darkMode
            ? '0 4px 12px rgba(0,0,0,0.15)'
            : '0 2px 8px rgba(0,0,0,0.08)',
          backdropFilter: 'blur(8px)',
          zIndex: 1,
        }}
      >
        <Box
          className="flex flex-wrap justify-start items-center gap-3"
          sx={{ flex: 1 }}
        >
          {timelineView === 'asset' && (
            <>
              <CheckboxesTags
                options={locations}
                selectedOptions={selectedLocations}
                handleChange={onLocationsChange}
                label="Locations"
                darkMode={darkMode}
                colorMap={locationColors}
              />
              <CheckboxesTags
                options={activityOptions}
                selectedOptions={selectedActivities}
                handleChange={onActivitiesChange}
                label="Activities"
                darkMode={darkMode}
                colorMap={{
                  Bolt: stringToHexColor('Bolt'),
                  Drill: stringToHexColor('Drill'),
                  Mucking: stringToHexColor('Bucket'),
                }}
                width={320}
              />
              <CheckboxesTags
                options={batteryOptions}
                selectedOptions={selectedBatteryInsights}
                handleChange={onBatteryInsightsChange}
                label="Battery Insights"
                darkMode={darkMode}
                colorMap={{
                  'Battery Energy Drained': '#f2765d',
                  'Battery Accumulated Energy': '#f2af11',
                  'Battery Charge Status': '#81d4fa',
                }}
                width={320}
              />
            </>
          )}
          {timelineView === 'location' && (
            <CheckboxesTags
              options={assets}
              selectedOptions={selectedAssets}
              handleChange={onAssetsChange}
              label="Assets"
              darkMode={darkMode}
              colorMap={assetColors}
            />
          )}
        </Box>
        <Box
          className="flex justify-end items-end gap-3 z-50"
          sx={{ minWidth: '320px' }}
        >
          <CustomDatePicker
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            onDateChange={onDateChange}
          />
          <GlencoreButton
            loading={loading}
            onClick={onFetch}
            text="Fetch Data"
            loadingText="Loading..."
          />
        </Box>
      </Box>
    </>
  );
};

export default FiltersBar;
